"use client";

import { useRouter, useSearchParams } from "next/navigation";
import { useState } from "react";
import { Search } from "lucide-react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Category } from "@/types/types";

type PostFiltersProps = {
  categories: Category[];
  activeCategory?: string;
};

export default function PostFilters({ categories, activeCategory }: PostFiltersProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [query, setQuery] = useState(searchParams.get("title") ?? "");

  const applySearch = () => {
    const params = new URLSearchParams(searchParams.toString());
    if (query.trim()) params.set("title", query.trim());
    else params.delete("title");
    params.delete("page");
    router.replace(`/posts?${params.toString()}`);
  };

  return (
    <div className="space-y-4 rounded-lg border border-border bg-card p-4">
      <div className="flex flex-wrap gap-2">
        <Link
          href="/posts"
          className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
            !activeCategory
              ? "bg-primary text-primary-foreground"
              : "bg-muted hover:bg-muted/80"
          }`}
        >
          All
        </Link>
        {categories.map((category) => (
          <Link
            key={category.slug}
            href={`/posts?category=${category.slug}`}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              activeCategory === category.slug
                ? "bg-primary text-primary-foreground"
                : "bg-muted hover:bg-muted/80"
            }`}
          >
            {category.name}
          </Link>
        ))}
      </div>
      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && applySearch()}
            placeholder="Search posts by title…"
            className="pl-9"
          />
        </div>
        <Button onClick={applySearch}>Search</Button>
      </div>
    </div>
  );
}
